'use client';

import React, { useMemo } from 'react';
import { cx } from '../utils/cx';

export interface BarChartProps {
  /**
   * Values:
   * - Prefer number[] for portability
   * - Legacy CSV string supported (e.g. "45,70,55,90,80")
   */
  data: number[] | string;

  /**
   * Labels:
   * - Prefer string[] for portability
   * - Legacy CSV string supported (e.g. "M,T,W,T,F")
   */
  labels?: string[] | string;

  /**
   * Color token/value for bars.
   * Prefer CSS color values or CSS variables (e.g. "var(--color-primary)").
   * @default "primary"
   */
  color?: string;

  /** Bar width in viewBox units. @default 20 */
  barWidth?: number;

  /** Rounded corners radius. @default 4 */
  barRadius?: number;

  /** Whether to show horizontal grid. @default true */
  showGrid?: boolean;

  /** Height in px. @default 120 */
  height?: number;

  /** Provide when chart conveys meaning; if omitted, chart is decorative. */
  ariaLabel?: string;

  className?: string;
}

function parseNumbers(input: number[] | string): number[] {
  if (Array.isArray(input)) return input.filter((n) => typeof n === 'number' && Number.isFinite(n));
  return String(input ?? '')
    .split(',')
    .map((s) => Number.parseFloat(s.trim()))
    .filter((n) => Number.isFinite(n));
}

function parseStrings(input: string[] | string | undefined): string[] {
  if (!input) return [];
  if (Array.isArray(input)) return input.map((s) => String(s).trim()).filter(Boolean);
  return String(input)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function isCssColorValue(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  return (
    v.startsWith('#') ||
    v.startsWith('rgb(') ||
    v.startsWith('rgba(') ||
    v.startsWith('hsl(') ||
    v.startsWith('hsla(') ||
    v.startsWith('var(')
  );
}

function resolveColorToken(token: string | undefined): string {
  const t = String(token ?? 'primary').trim();
  if (!t) return 'currentColor';
  if (isCssColorValue(t)) return t;
  // Best-effort token -> CSS var
  return `var(--color-${t}, currentColor)`;
}

export function BarChart({
  data,
  labels,
  color = 'primary',
  barWidth = 20,
  barRadius = 4,
  showGrid = true,
  height = 120,
  ariaLabel,
  className,
}: BarChartProps) {
  const values = useMemo(() => parseNumbers(data), [data]);
  const labelList = useMemo(() => parseStrings(labels), [labels]);

  const colorValue = useMemo(() => resolveColorToken(color), [color]);

  const chart = useMemo(() => {
    const n = values.length;
    if (n === 0) return null;

    const gap = Math.max(6, Math.round(barWidth * 0.45));
    const paddingX = 10;
    const paddingTop = 10;
    const labelArea = labelList.length > 0 ? 22 : 8;

    const totalW = paddingX * 2 + n * barWidth + (n - 1) * gap;
    const totalH = 100; // viewBox height units
    const barsH = totalH - paddingTop - labelArea;

    const max = Math.max(1, ...values.map((v) => Math.max(0, v)));

    const bars = values.map((v, i) => {
      const safe = Math.max(0, v);
      const h = (safe / max) * barsH;
      const x = paddingX + i * (barWidth + gap);
      const y = paddingTop + (barsH - h);

      return <rect key={`bar-${i}`} x={x} y={y} width={barWidth} height={h} rx={barRadius} fill={colorValue} />;
    });

    const grid = showGrid
      ? [0.25, 0.5, 0.75].map((p) => {
          const y = paddingTop + barsH * (1 - p);
          return (
            <line
              key={`g-${p}`}
              x1={paddingX}
              x2={totalW - paddingX}
              y1={y}
              y2={y}
              stroke="currentColor"
              strokeOpacity={0.12}
              strokeWidth={1}
            />
          );
        })
      : null;

    const xLabels =
      labelList.length > 0
        ? values.map((_, i) => {
            const txt = labelList[i] ?? '';
            if (!txt) return null;
            const x = paddingX + i * (barWidth + gap) + barWidth / 2;
            const y = paddingTop + barsH + 16;
            return (
              <text key={`lbl-${i}`} x={x} y={y} textAnchor="middle" fontSize="10" fill="currentColor" opacity={0.55}>
                {txt}
              </text>
            );
          })
        : null;

    return { totalW, totalH, bars, grid, xLabels };
  }, [barRadius, barWidth, colorValue, labelList, showGrid, values]);

  if (!chart) return null;

  const a11yProps = ariaLabel
    ? ({ role: 'img', 'aria-label': ariaLabel } as const)
    : ({ role: 'presentation', 'aria-hidden': true } as const);

  return (
    <svg
      {...a11yProps}
      className={cx('w-full text-tertiary', className)}
      viewBox={`0 0 ${chart.totalW} ${chart.totalH}`}
      height={height}
      preserveAspectRatio="xMidYMid meet"
    >
      {ariaLabel ? <title>{ariaLabel}</title> : null}
      {chart.grid}
      {chart.bars}
      {chart.xLabels}
    </svg>
  );
}

BarChart.displayName = 'BarChart';
